import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Building2, User, Mail, Phone, MapPin, FileText, Key, Clock, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateCompany = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // États du formulaire
  const [step, setStep] = useState(1); // 1: Entreprise, 2: Super Admin
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Données entreprise
  const [companyData, setCompanyData] = useState({
    name: '',
    code: '',
    country: '',
    contact_email: '',
    contact_phone: '',
    description: ''
  });
  
  // Données super admin
  const [adminData, setAdminData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  
  const [companyId, setCompanyId] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/companies/create`, companyData);
      
      setCompanyId(response.data.company_id);
      setSuccess(t('companyCreatedSuccess'));
      setStep(2);
      
    } catch (err) {
      console.error('Error creating company:', err);
      setError(err.response?.data?.detail || t('createCompanyError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation mot de passe
    if (adminData.password !== adminData.confirmPassword) {
      setError(t('passwordsMismatch'));
      return;
    }

    if (adminData.password.length < 6) {
      setError(t('passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/companies/${companyId}/super-admin`, {
        name: adminData.name,
        email: adminData.email,
        password: adminData.password,
        phone: adminData.phone
      });
      
      setSuccess(t('superAdminCreatedSuccess'));
      setPendingApproval(true);
      
    } catch (err) {
      console.error('Error creating super admin:', err);
      setError(err.response?.data?.detail || t('createSuperAdminError'));
    } finally {
      setLoading(false);
    }
  };

  const countries = [
    'France', 'Allemagne', 'Espagne', 'Italie', 'Royaume-Uni', 
    'Belgique', 'Pays-Bas', 'Suisse', 'Portugal', 'Autriche'
  ];

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center py-6 sm:py-12 px-3 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center">
          <Building2 className="mx-auto h-12 w-12 text-accent" />
          <h2 className="mt-4 sm:mt-6 text-2xl sm:text-3xl font-extrabold text-foreground">
            {t('createYourCompany')}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('stepXofY').replace('{step}', step).replace('{total}', '2')} : {step === 1 ? t('companyInfoStep') : t('superAdminStep')}
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-accent h-2 rounded-full transition-all duration-300"
            style={{ width: `${(step / 2) * 100}%` }}
          />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-500/10 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-accent" />
                {t('companyInfoTitle')}
              </CardTitle>
              <CardDescription>
                {t('companyInfoDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCompanySubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('companyNameRequired')}
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="text"
                        required
                        value={companyData.name}
                        onChange={(e) => setCompanyData({...companyData, name: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="Transport Express SARL"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('companyCodeRequired')}
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="text"
                        required
                        value={companyData.code}
                        onChange={(e) => setCompanyData({...companyData, code: e.target.value.toUpperCase()})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="TREX"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('countryRequired')}
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <select
                        required
                        value={companyData.country}
                        onChange={(e) => setCompanyData({...companyData, country: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                      >
                        <option value="">{t('selectCountry')}</option>
                        {countries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('contactEmailRequired')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="email"
                        required
                        value={companyData.contact_email}
                        onChange={(e) => setCompanyData({...companyData, contact_email: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="contact@transport-express.fr"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('phoneOptional')}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="tel"
                        value={companyData.contact_phone}
                        onChange={(e) => setCompanyData({...companyData, contact_phone: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('descriptionOptional')}
                  </label>
                  <textarea
                    value={companyData.description}
                    onChange={(e) => setCompanyData({...companyData, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder={t('descriptionPlaceholder')}
                  />
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={loading} className="w-full sm:w-auto">
                    {loading ? t('creating') : t('continue')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-accent" />
                {t('superAdminTitle')}
              </CardTitle>
              <CardDescription>
                {t('superAdminDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAdminSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('fullNameRequired')}
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="text"
                        required
                        value={adminData.name}
                        onChange={(e) => setAdminData({...adminData, name: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="Jean Dupont"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('emailRequired')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="email"
                        required
                        value={adminData.email}
                        onChange={(e) => setAdminData({...adminData, email: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="admin@transport-express.fr"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('passwordRequired')}
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="password"
                        required
                        value={adminData.password}
                        onChange={(e) => setAdminData({...adminData, password: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder={t('passwordMin6')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('confirmPasswordRequired')}
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="password"
                        required
                        value={adminData.confirmPassword}
                        onChange={(e) => setAdminData({...adminData, confirmPassword: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder={t('confirmPasswordPlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t('phoneOptional')}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="tel"
                        value={adminData.phone}
                        onChange={(e) => setAdminData({...adminData, phone: e.target.value})}
                        className="pl-10 w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setStep(1)}
                    disabled={loading}
                  >
                    {t('back')}
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? t('creating') : t('createCompanyButton')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Pending approval message */}
        {pendingApproval && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-100">
                  <Clock className="h-8 w-8 text-amber-600" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-amber-900">
                    {t('companyPendingTitle') || 'En attente de validation'}
                  </h3>
                  <p className="text-sm text-amber-700 max-w-md">
                    {t('companyPendingMessage') || 'Votre entreprise a été créée avec succès. Elle est en attente de validation par l\'administrateur de la plateforme. Vous recevrez un email une fois approuvée.'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="mt-4"
                >
                  {t('backToLogin') || 'Retour à la connexion'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lien vers login */}
        {!pendingApproval && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {t('alreadyHaveAccount')}{' '}
              <button
                onClick={() => navigate('/login')}
                className="font-medium text-accent hover:text-accent/80"
              >
                {t('signIn')}
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateCompany;